import { Box, Flex, useColorModeValue } from '@chakra-ui/react';
import { useEffect, useMemo, useRef } from 'react';

export interface InfiniteCarouselProps {
  content: JSX.Element[];
  /** Duration (seconds) for one full loop */
  speed?: number;
  /** Gap in px between items */
  itemGap?: number;
  /** Pauses on hover for pointer+hover devices only */
  pauseOnHover?: boolean;
  enableEdgeFade?: boolean;
}

export default function InfiniteCarousel({
  content,
  speed = 30,
  itemGap = 16,
  pauseOnHover = true,
  enableEdgeFade = false
}: InfiniteCarouselProps) {
  const fadeGradientLight = useColorModeValue(
    {
      base:
        'linear-gradient(to right, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 20%, rgba(255, 255, 255, 0) 80%, rgba(255, 255, 255, 1) 100%)',
      md:
        'linear-gradient(to right, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 10%, rgba(255, 255, 255, 0) 90%, rgba(255, 255, 255, 1) 100%)'
    },
    {
      base:
        'linear-gradient(to right, rgba(26, 32, 44, 1) 0%, rgba(26, 32, 44, 0) 20%, rgba(26, 32, 44, 0) 80%, rgba(26, 32, 44, 1) 100%)',
      md:
        'linear-gradient(to right, rgba(26, 32, 44, 1) 0%, rgba(26, 32, 44, 0) 10%, rgba(26, 32, 44, 0) 90%, rgba(26, 32, 44, 1) 100%)'
    }
  );

  // Duplicate content once for seamless loop
  const duplicatedContent = useMemo(() => [...content, ...content], [content]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const track = trackRef.current;
    if (!wrapper || !track) return;

    const setDistance = () => {
      // distance = half of track’s full scroll width
      const dist = track.scrollWidth / 2;
      track.style.setProperty('--scroll-distance', `${dist}px`);
      track.style.setProperty('--scroll-duration', `${speed}s`);
    };

    setDistance();

    // Recompute on resize/orientation changes
    const ro = new ResizeObserver(() => setDistance());
    ro.observe(track);
    ro.observe(wrapper);

    return () => ro.disconnect();
  }, [speed, content.length]);

  return (
    <Box position="relative" width="100%" overflow="hidden" ref={wrapperRef}>
      {enableEdgeFade && (
        <Box
          position="absolute"
          inset={0}
          background={fadeGradientLight}
          pointerEvents="none"
          zIndex={2}
        />
      )}

      <Flex
        ref={trackRef}
        as="div"
        gap={`${itemGap}px`}
        // Always left-align; center causes seams when the animation resets
        justifyContent="flex-start"
        // Make the track size to its content, not the container
        width="max-content"
        // Animation + mobile/touch fixes
        sx={{
          // prefers-reduced-motion: just don’t animate
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none'
          },
          // Only pause on hover when hover is supported
          '@media (hover: hover) and (pointer: fine)': pauseOnHover
            ? {
                '&:hover': {
                  animationPlayState: 'paused'
                }
              }
            : {},
          animation: 'marquee var(--scroll-duration, 30s) linear infinite',
          willChange: 'transform',
          '@keyframes marquee': {
            '0%': { transform: 'translate3d(0, 0, 0)' },
            '100%': { transform: 'translate3d(calc(-1 * var(--scroll-distance, 0px)), 0, 0)' }
          }
        }}
      >
        {duplicatedContent.map((item, index) => (
          <Box key={`infinite_carousel_item_${index}`} flexShrink={0}>
            {/* Mark the duplicated half as hidden from assistive tech */}
            {index >= content.length ? <Box aria-hidden="true">{item}</Box> : item}
          </Box>
        ))}
      </Flex>
    </Box>
  );
}
